import { useState } from 'react';

import config from '@/config';
import useDoubleTapHandle from '@/hooks/useDoubleTapHandle';
import useProtectAction from '@/hooks/useProtectAction';
import DoubleTabLike from '@/shared-components/animations/components/DoubleTabLike';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import './styles/ArtworkImage.css';

// A card can remount when returning to a feed even though its image was already
// loaded in this tab. Remember those URLs so the placeholder does not flash.
const loadedImageUrls = new Set();
const MAX_LOADED_IMAGE_URLS = 500;
const RESPONSIVE_IMAGE_WIDTHS = [200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200];

function rememberLoadedImage(url) {
  loadedImageUrls.delete(url);
  loadedImageUrls.add(url);
  if (loadedImageUrls.size > MAX_LOADED_IMAGE_URLS) {
    loadedImageUrls.delete(loadedImageUrls.values().next().value);
  }
}

export default function ArtworkImage({
  image,
  imageTemplateClassName = "",
  imageClassName = "",
  style = {},
  onClick = () => {},
  onDoubleClick = () => {},
  forceSmaller,
  forceSmallerDimension = "width",
  responsiveSizes,
  size = 2000,
  artworkData,
  alt,
  enabledDoubleClick = true,
  disableDownload = true,
}) {
  const [loadedImageUrl, setLoadedImageUrl] = useState(null);
  const [doubleTabLike, setDoubleTabLike] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });

  const doubleLike = () => {
    setDoubleTabLike(true);
    setTimeout(() => {
      setDoubleTabLike(false);
    }, 1000);
  };

  const doubleTapFunctions = () => {
    doubleLike();
    onDoubleClick();
  };

  const handleMousePosition = (event) => {
    setPosition({ x: event.clientX, y: event.clientY });
  };

  const manageClick = useDoubleTapHandle(onClick, () => privateActionHandler(doubleTapFunctions), enabledDoubleClick);

  const getAltText = (data) => {
    if (alt) {
      return alt;
    }
    if (data?.artwork_about) {
      const {
        artwork_title: title = "Title",
        artwork_year: year = "",
        artwork_size: { height, length, unit = "cm" } = {},
        artwork_color = [],
        artwork_theme = [],
        artwork_feeling = [],
        artwork_medium: medium
      } = data.artwork_about || {};
      const resolvedArtist = getArtworkArtistDisplayName(data);
      const artist = resolvedArtist && resolvedArtist !== "-" ? resolvedArtist : "Artist";

      const size = `${height}${unit} X ${length}${unit}`;
      const colors = artwork_color.join(", ");
      const emotions = artwork_feeling.join(", ");
      const themes = artwork_theme.join(", ");
      const altText = `${title}, artwork by ${artist}, ${year}. ${size}, ${medium}. ${colors}, ${emotions}, ${themes}. `;
      return altText;
    }
    return "Image in Suarte";
  };

  // Check if image is a blob URL
  const isBlobURL = typeof image === "string" && (image.startsWith("blob:") || image.startsWith("data:image/"));
  const hasImage = typeof image === "string" && image.trim() !== "" && image !== "undefined" && image !== "null";
  const imageUrl = isBlobURL
    ? image
    : forceSmaller
      ? `${config.app.imageServiceDomain}/${image}/${forceSmallerDimension === "width" ? "w" : "h"}=${forceSmaller}`
      : `${config.app.imageServiceDomain}/${image}/w=${size}`;
  const imageSrcSet = !hasImage || isBlobURL ? undefined : RESPONSIVE_IMAGE_WIDTHS
    .map((width) => `${config.app.imageServiceDomain}/${image}/w=${width} ${width}w`)
    .join(', ');
  const imageLoaded = loadedImageUrl === imageUrl || (!isBlobURL && loadedImageUrls.has(imageUrl));
  const handleImageLoad = () => {
    if (!isBlobURL) rememberLoadedImage(imageUrl);
    setLoadedImageUrl(imageUrl);
  };
  const protectedStyle = disableDownload
    ? { ...style, WebkitTouchCallout: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none', userSelect: 'none' }
    : style;

  if (!hasImage) {
    return (
      <div
        role="img"
        aria-label={getAltText(artworkData)}
        className={`artwork-image-template ${imageTemplateClassName} ${imageClassName}`}
        onClick={(event) => {
          manageClick();
          handleMousePosition(event);
        }}
        onContextMenu={disableDownload ? (event) => event.preventDefault() : undefined}
        onDragStart={disableDownload ? (event) => event.preventDefault() : undefined}
        style={protectedStyle}
      />
    );
  }

  return (
    <>
      {!forceSmaller ? (
        <>
          <img
            src={imageUrl}
            srcSet={imageSrcSet}
            loading={forceSmaller ? "lazy" : undefined}
            decoding={forceSmaller ? "async" : undefined}
            alt={getAltText(artworkData)}
            className={!imageLoaded ? "artwork-image-loading" : imageClassName}
            onLoad={handleImageLoad}
            onClick={(e) => {
              manageClick();
              handleMousePosition(e);
            }}
            onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
            onDragStart={disableDownload ? (e) => e.preventDefault() : undefined}
            draggable={disableDownload ? false : undefined}
            style={protectedStyle}
          />
          <DoubleTabLike position={position} className="artwork-image-double-tap-like__button" open={doubleTabLike} />
        </>
      ) : (
        <img
          src={imageUrl}
          srcSet={responsiveSizes ? imageSrcSet : undefined}
          sizes={responsiveSizes}
          loading="lazy"
          decoding="async"
          alt={getAltText(artworkData)}
          className={!imageLoaded ? "artwork-image-loading" : imageClassName}
          onLoad={handleImageLoad}
          onClick={manageClick}
          onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
          onDragStart={disableDownload ? (e) => e.preventDefault() : undefined}
          draggable={disableDownload ? false : undefined}
          style={protectedStyle}
        />
      )}
      {!imageLoaded && (
        <div className={`artwork-image-template ${imageTemplateClassName}`} style={style} />
      )}
    </>
  );
}
